import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { fetchWithCorpId, fetchWithAuth } from '../utils/api';

const countFrom = (data) => {
  if (!data) return '—';
  if (Array.isArray(data)) return data.length;
  if (data.totalElements !== undefined) return data.totalElements;
  if (data.content && Array.isArray(data.content)) return data.content.length;
  return '—';
};

const toArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.content && Array.isArray(data.content)) return data.content;
  return [];
};

const safe = (fn) => fn().catch(() => null);

const ROLE_META = {
  super:    { label: 'Super Administrator', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  admin:    { label: 'Administrator',       color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  employer: { label: 'Employer',            color: '#065f46', bg: '#ecfdf5', border: '#a7f3d0' },
  employee: { label: 'Employee',            color: '#92400e', bg: '#fffbeb', border: '#fde68a' },
};

const StatCard = ({ label, value, icon, accentColor, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '22px 20px',
      display: 'flex', alignItems: 'center', gap: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.18s, transform 0.18s',
      flex: '1 1 180px',
      minWidth: '160px',
    }}
    onMouseOver={e => { if (onClick) { e.currentTarget.style.boxShadow = `0 6px 20px ${accentColor}22`; e.currentTarget.style.transform = 'translateY(-2px)'; }}}
    onMouseOut={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    <div style={{
      width: '48px', height: '48px', borderRadius: '11px',
      background: `${accentColor}15`, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ color: accentColor }}>{icon}</span>
    </div>
    <div>
      <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '26px', fontWeight: '800', color: '#1e293b', lineHeight: 1 }}>
        {value === null ? (
          <span style={{ display: 'inline-block', width: '40px', height: '20px', background: '#f1f5f9', borderRadius: '4px', animation: 'pulse 1.5s ease infinite' }} />
        ) : value}
      </div>
    </div>
  </div>
);

const ActionBtn = ({ label, path, color, icon }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 18px', borderRadius: '8px',
        border: `1px solid ${color}30`, background: `${color}0d`,
        color: color, cursor: 'pointer',
        fontSize: '13px', fontWeight: '600',
        transition: 'all 0.15s ease',
      }}
      onMouseOver={e => { e.currentTarget.style.background = `${color}1f`; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${color}28`; }}
      onMouseOut={e => { e.currentTarget.style.background = `${color}0d`; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {icon}
      {label}
    </button>
  );
};

const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ flex: 1, height: '1px', background: '#f1f5f9' }} />
  </div>
);

// Icons
const Icons = {
  corp: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  vendor: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  employee: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  invoice: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  billing: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  timesheet: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  admin: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  plus: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  check: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  clock: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

const Dashboard = () => {
  const { getUserRoles, isSuperUser, token } = useAuth();
  const navigate = useNavigate();
  const roles = getUserRoles();
  const isSuper = isSuperUser();
  const isAdmin = roles.includes('admin');
  const isEmployer = roles.includes('employer');
  const isEmployee = roles.includes('employee');

  const primaryRole = isSuper ? 'super' : isAdmin ? 'admin' : isEmployer ? 'employer' : 'employee';
  const roleMeta = ROLE_META[primaryRole];

  const parseJwt = (t) => { try { return JSON.parse(atob(t.split('.')[1])); } catch { return null; } };
  const tokenData = token ? parseJwt(token) : null;
  const username = tokenData?.sub || tokenData?.given_name || 'User';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const [stats, setStats] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      const s = {};
      if (isSuper) {
        const [corps, vendors, employees, admins] = await Promise.all([
          safe(() => fetchWithAuth('/corps')),
          safe(() => fetchWithCorpId('/vendors')),
          safe(() => fetchWithCorpId('/employees')),
          safe(() => fetchWithCorpId('/admin')),
        ]);
        s.corps     = corps     !== null ? countFrom(corps)     : '—';
        s.vendors   = vendors   !== null ? countFrom(vendors)   : '—';
        s.employees = employees !== null ? countFrom(employees) : '—';
        s.admins    = admins    !== null ? countFrom(admins)    : '—';
      } else if (isAdmin) {
        const [vendors, employees, timesheets, invoices, billings] = await Promise.all([
          safe(() => fetchWithCorpId('/vendors')),
          safe(() => fetchWithCorpId('/employees')),
          safe(() => fetchWithCorpId('/timesheets')),
          safe(() => fetchWithCorpId('/invoices')),
          safe(() => fetchWithCorpId('/billings')),
        ]);
        s.vendors    = vendors    !== null ? countFrom(vendors)    : '—';
        s.employees  = employees  !== null ? countFrom(employees)  : '—';
        s.timesheets = timesheets !== null ? countFrom(timesheets) : '—';
        s.invoices   = invoices   !== null ? countFrom(invoices)   : '—';
        s.billings   = billings   !== null ? countFrom(billings)   : '—';
      } else if (isEmployer) {
        const [employees, invoices, timesheets] = await Promise.all([
          safe(() => fetchWithCorpId('/employees')),
          safe(() => fetchWithCorpId('/invoices')),
          safe(() => fetchWithCorpId('/timesheets')),
        ]);
        s.employees  = employees  !== null ? countFrom(employees)  : '—';
        s.timesheets = timesheets !== null ? countFrom(timesheets) : '—';
        if (invoices !== null) {
          const arr = toArray(invoices);
          s.invoices        = arr.length;
          s.pendingInvoices = arr.filter(i => (i.status || '').toLowerCase() === 'pending').length;
        } else {
          s.invoices = '—'; s.pendingInvoices = '—';
        }
      } else {
        const [timesheets] = await Promise.all([
          safe(() => fetchWithCorpId('/timesheets')),
        ]);
        if (timesheets !== null) {
          const arr = toArray(timesheets);
          s.timesheets         = arr.length;
          s.pendingTimesheets  = arr.filter(t => (t.status || '').toLowerCase() === 'pending').length;
          s.approvedTimesheets = arr.filter(t => (t.status || '').toLowerCase() === 'approved').length;
        } else {
          s.timesheets = '—'; s.pendingTimesheets = '—'; s.approvedTimesheets = '—';
        }
      }
      setStats(s);
      setLoaded(true);
    };
    load();
  }, []);

  const v = (key) => loaded ? stats[key] ?? '—' : null;

  return (
    <div style={{ padding: '32px', maxWidth: '1100px', margin: '0 auto' }}>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>

      {/* Header */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px',
        padding: '28px 32px', marginBottom: '28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>{today}</div>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: '800', color: '#1e293b', letterSpacing: '-0.3px' }}>
            Dashboard
          </h1>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            Welcome back, <strong>{username}</strong>
          </div>
        </div>
        <div style={{
          padding: '8px 16px', borderRadius: '8px',
          background: roleMeta.bg, border: `1px solid ${roleMeta.border}`,
          fontSize: '12px', fontWeight: '700', color: roleMeta.color,
          letterSpacing: '0.3px', textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>
          {roleMeta.label}
        </div>
      </div>

      {/* Stats */}
      <SectionLabel text="Overview" />
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '32px' }}>

        {isSuper && <>
          <StatCard label="Corporations" value={v('corps')}     accentColor="#7c3aed" icon={Icons.corp}      onClick={() => navigate('/on/corps')} />
          <StatCard label="Vendors"      value={v('vendors')}   accentColor="#3b82f6" icon={Icons.vendor}    onClick={() => navigate('/on/vendors')} />
          <StatCard label="Employees"    value={v('employees')} accentColor="#10b981" icon={Icons.employee}  onClick={() => navigate('/on/employees')} />
          <StatCard label="Admins"       value={v('admins')}    accentColor="#f59e0b" icon={Icons.admin}     onClick={() => navigate('/on/admin')} />
        </>}

        {!isSuper && isAdmin && <>
          <StatCard label="Vendors"    value={v('vendors')}    accentColor="#3b82f6" icon={Icons.vendor}    onClick={() => navigate('/on/vendors')} />
          <StatCard label="Employees"  value={v('employees')}  accentColor="#10b981" icon={Icons.employee}  onClick={() => navigate('/on/employees')} />
          <StatCard label="Timesheets" value={v('timesheets')} accentColor="#ec4899" icon={Icons.timesheet} onClick={() => navigate('/on/timesheets')} />
          <StatCard label="Invoices"   value={v('invoices')}   accentColor="#f59e0b" icon={Icons.invoice}   onClick={() => navigate('/on/invoices')} />
          <StatCard label="Billings"   value={v('billings')}   accentColor="#8b5cf6" icon={Icons.billing}   onClick={() => navigate('/on/billings')} />
        </>}

        {!isSuper && !isAdmin && isEmployer && <>
          <StatCard label="Employees"       value={v('employees')}      accentColor="#10b981" icon={Icons.employee}  onClick={() => navigate('/on/employees')} />
          <StatCard label="Total Invoices"  value={v('invoices')}       accentColor="#f59e0b" icon={Icons.invoice}   onClick={() => navigate('/on/invoices')} />
          <StatCard label="Pending Invoices" value={v('pendingInvoices')} accentColor="#ef4444" icon={Icons.clock}   />
          <StatCard label="Timesheets"      value={v('timesheets')}     accentColor="#ec4899" icon={Icons.timesheet} onClick={() => navigate('/on/timesheets')} />
        </>}

        {!isSuper && !isAdmin && !isEmployer && isEmployee && <>
          <StatCard label="My Timesheets"  value={v('timesheets')}         accentColor="#3b82f6" icon={Icons.timesheet} onClick={() => navigate('/on/timesheets')} />
          <StatCard label="Pending"        value={v('pendingTimesheets')}  accentColor="#f59e0b" icon={Icons.clock} />
          <StatCard label="Approved"       value={v('approvedTimesheets')} accentColor="#10b981" icon={Icons.check} />
        </>}
      </div>

      {/* Quick Actions */}
      <SectionLabel text="Quick Actions" />
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {isSuper && <>
          <ActionBtn label="New Corporation" path="/on/corps/new"     color="#7c3aed" icon={Icons.plus} />
          <ActionBtn label="New Vendor"      path="/on/vendors/new"   color="#3b82f6" icon={Icons.plus} />
          <ActionBtn label="New Employee"    path="/on/employees/new" color="#10b981" icon={Icons.plus} />
          <ActionBtn label="New Invoice"     path="/on/invoices/new"  color="#f59e0b" icon={Icons.plus} />
        </>}

        {!isSuper && isAdmin && <>
          <ActionBtn label="New Vendor"   path="/on/vendors/new"   color="#3b82f6" icon={Icons.plus} />
          <ActionBtn label="New Employee" path="/on/employees/new" color="#10b981" icon={Icons.plus} />
          <ActionBtn label="New Invoice"  path="/on/invoices/new"  color="#f59e0b" icon={Icons.plus} />
          <ActionBtn label="New Billing"  path="/on/billings/new"  color="#8b5cf6" icon={Icons.plus} />
        </>}

        {!isSuper && !isAdmin && isEmployer && <>
          <ActionBtn label="New Employee"  path="/on/employees/new"   color="#10b981" icon={Icons.plus} />
          <ActionBtn label="New Invoice"   path="/on/invoices/new"    color="#f59e0b" icon={Icons.plus} />
          <ActionBtn label="New Timesheet" path="/on/timesheets/new"  color="#ec4899" icon={Icons.plus} />
        </>}

        {!isSuper && !isAdmin && !isEmployer && isEmployee && <>
          <ActionBtn label="New Timesheet"      path="/on/timesheets/new"   color="#3b82f6" icon={Icons.plus} />
          <ActionBtn label="View Timesheet Items" path="/on/timesheet-items" color="#10b981" icon={Icons.timesheet} />
        </>}
      </div>
    </div>
  );
};

export default Dashboard;
